import React from 'react'
import { useDataSource } from '@/src/renderer/lib/dataSource'
import './MemoAuthor.scss'
import { WithTooltip } from '@/src/renderer/components/ui/tooltip'

export interface MemoAuthorProps {
  /** Der Grund der Pille — aus der Palette des Memos. */
  chipColor: string
  /** Die Schriftfarbe, dieselbe wie im Text des Memos. */
  textColor: string
  /**
   * Wie viel gezeigt wird.
   *
   * `pill` ist die volle Pille der **Memo** — Avatar und Vorname. `avatar`
   * zeigt nur den Avatar und trägt den Namen im Tooltip: So steht der Autor am
   * **Notizen**, der bis auf 72px schrumpfen darf und für die Pille (26px
   * hoch, 90–120px breit) keinen Platz hätte.
   */
  variant?: 'pill' | 'avatar'
}

/**
 * Der Autor einer **Memo** — Avatar und Vorname in einer Pille unter dem Text
 * (Figma 4869:95003, `Username`).
 *
 * **Nur der Vorname.** Der Zettel ist klein, und auf einem Canvas, an dem
 * wenige Menschen arbeiten, unterscheidet er genug; der volle Name drängte den
 * Text zur Seite.
 *
 * **Immer der eigene**, aus dem Profil gelesen und nicht in des Memos
 * gespeichert: Solange es keine Freigabe gibt, gibt es keinen anderen — und
 * ein gespeicherter Name veraltete bei der ersten Umbenennung. Wenn die
 * Freigabe kommt, liest diese Stelle den Autor der Zeile statt den eigenen,
 * und sonst ändert sich nichts.
 *
 * @example
 * <MemoAuthor chipColor={color.chip} textColor={color.text} />
 * <MemoAuthor variant="avatar" chipColor={hex} textColor="inherit" />
 */
export const MemoAuthor: React.FC<MemoAuthorProps> = ({ chipColor, textColor, variant = 'pill' }) => {
  const { avatarUrl, fullName, isLoading } = useDataSource().useUserDisplayProfile(24)

  // Ohne Namen keine Pille: Ein leerer Kasten sagt nichts und nimmt dem Text
  // Platz weg. Während des Ladens ebenso — sie soll nicht erst erscheinen und
  // dann den Namen nachreichen.
  if (isLoading || !fullName) return null

  const firstName = fullName.trim().split(/\s+/)[0]

  return (
    <WithTooltip content={variant === 'avatar' ? firstName : undefined}>
      <div
        className={`memo-author${variant === 'avatar' ? ' memo-author--avatar' : ''}`}
        style={{ '--author-chip': chipColor, '--author-text': textColor } as React.CSSProperties}
      >
        {avatarUrl && (
          <img className="memo-author__avatar" src={avatarUrl} alt="" draggable={false} />
        )}
        {variant === 'pill' && <span className="memo-author__name">{firstName}</span>}
      </div>
    </WithTooltip>
  )
}
